import { DateTime } from 'luxon';

/** US Eastern — all internal dashboard clocks use this zone (EST / EDT per date rules). */
export const DASHBOARD_TZ = 'America/New_York';

const DEFAULT_SLOT_YEAR = 2026;

const MONTH_ABBREV_TO_NUM = {
  Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
  Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12
};

export const MONTH_FULL_TO_NUM = {
  January: 1, February: 2, March: 3, April: 4, May: 5, June: 6,
  July: 7, August: 8, September: 9, October: 10, November: 11, December: 12
};

function ianaFromStudentTimezoneLabel(label) {
  const t = String(label || '');
  if (t.includes('Pacific')) return 'America/Los_Angeles';
  if (t.includes('Mountain')) return 'America/Denver';
  if (t.includes('Central') && !t.includes('European')) return 'America/Chicago';
  return 'America/New_York';
}

function ianaFromTzAbbrev(abbr) {
  const a = String(abbr || '').replace(/\s+/g, '').toUpperCase();
  if (!a) return null;
  if (/^(EDT|EST|ET)$/.test(a)) return 'America/New_York';
  if (/^(PDT|PST|PT)$/.test(a)) return 'America/Los_Angeles';
  if (/^(CDT|CST|CT)$/.test(a)) return 'America/Chicago';
  if (/^(MDT|MST|MT)$/.test(a)) return 'America/Denver';
  return null;
}

function hour12To24(h12, meridiem) {
  const h = parseInt(h12, 10);
  const p = String(meridiem || '').toUpperCase();
  if (p === 'AM') return h === 12 ? 0 : h;
  return h === 12 ? 12 : h + 12;
}

function stripSlotParenthetical(s) {
  return String(s || '').replace(/\s*\([^)]*\)\s*$/g, '').trim();
}

export function firstCalendarDate2026Weekday(dayFullName) {
  const map = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
  const target = map[dayFullName];
  if (target === undefined) return null;
  const d = new Date(2026, 0, 1);
  const diff = (target - d.getDay() + 7) % 7;
  d.setDate(d.getDate() + diff);
  const abbrevs = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return {
    dayAbbrev: abbrevs[d.getDay()],
    monthFull: MONTH_FULL[d.getMonth()],
    dayNum: d.getDate()
  };
}

function monthNumFromFullName(name) {
  const n = String(name || '').trim().toLowerCase();
  const key = Object.keys(MONTH_FULL_TO_NUM).find((k) => k.toLowerCase() === n);
  return key ? MONTH_FULL_TO_NUM[key] : null;
}

/** "Mon, June 8 @ 9:30AM PT" — wall clock is in PT (or suffix); converted to Eastern for display. */
function parseFirstClassLineToEasternDateTime(str, studentTimezoneLabel) {
  const p = String(str || '').trim();
  let m = p.match(/^(Mon|Tue|Wed|Thu|Fri|Sat|Sun),\s+([A-Za-z]+)\s+(\d{1,2})\s+@\s+(\d{1,2})(?::(\d{2}))?\s*(AM|PM)\s*([A-Z]{2,4})?$/i);
  if (!m) {
    m = p.match(/^(Mon|Tue|Wed|Thu|Fri|Sat|Sun),\s+([A-Za-z]+)\s+(\d{1,2})\s+@\s+(\d{1,2})(?::(\d{2}))?\s*(AM|PM)\s*([A-Z]{2,4})?/i);
  }
  if (!m) return null;
  const monthNum = monthNumFromFullName(m[2]);
  if (!monthNum) return null;
  const dayNum = parseInt(m[3], 10);
  const min = m[5] !== undefined ? parseInt(m[5], 10) : 0;
  const hour24 = hour12To24(m[4], m[6]);
  const zoneAbbr = m[7] ? m[7].trim() : '';
  const zone = ianaFromTzAbbrev(zoneAbbr) || ianaFromStudentTimezoneLabel(studentTimezoneLabel);
  const dt = DateTime.fromObject(
    { year: DEFAULT_SLOT_YEAR, month: monthNum, day: dayNum, hour: hour24, minute: min, second: 0 },
    { zone }
  );
  if (!dt.isValid) return null;
  return dt.setZone(DASHBOARD_TZ);
}

export function parseSlotStringToEasternDateTime(str, studentTimezoneLabel) {
  const s = stripSlotParenthetical(str);
  let m = s.match(/^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)[^A-Za-z0-9]*([A-Za-z]{3})\s+(\d{1,2})\s+at\s+(\d{1,2}):(\d{2})\s*(AM|PM)\s*([A-Z]{2,4})?/i);
  if (m) {
    const monKey = m[2].charAt(0).toUpperCase() + m[2].slice(1, 3).toLowerCase();
    const monthNum = MONTH_ABBREV_TO_NUM[monKey];
    if (!monthNum) return null;
    const day = parseInt(m[3], 10);
    const hour24 = hour12To24(m[4], m[6]);
    const minute = parseInt(m[5], 10);
    const zoneAbbr = m[7] ? m[7].trim() : '';
    const zone = ianaFromTzAbbrev(zoneAbbr) || ianaFromStudentTimezoneLabel(studentTimezoneLabel);
    const dt = DateTime.fromObject(
      { year: DEFAULT_SLOT_YEAR, month: monthNum, day, hour: hour24, minute, second: 0 },
      { zone }
    );
    if (!dt.isValid) return null;
    return dt.setZone(DASHBOARD_TZ);
  }
  m = s.match(/^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s+(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (m) {
    const cal = firstCalendarDate2026Weekday(m[1]);
    if (!cal) return null;
    const monthNum = MONTH_FULL_TO_NUM[cal.monthFull];
    if (!monthNum) return null;
    const hour24 = hour12To24(m[2], m[4]);
    const minute = parseInt(m[3], 10);
    const zone = ianaFromStudentTimezoneLabel(studentTimezoneLabel);
    const dt = DateTime.fromObject(
      { year: DEFAULT_SLOT_YEAR, month: monthNum, day: cal.dayNum, hour: hour24, minute, second: 0 },
      { zone }
    );
    if (!dt.isValid) return null;
    return dt.setZone(DASHBOARD_TZ);
  }
  return null;
}

/**
 * Parse stored First class line — respects PT/CT/MT/ET suffix (or student timezone if omitted).
 */
export function parsePrimaryStringToEasternDateTime(primaryStr, studentTimezoneLabel = 'Eastern Time (ET)') {
  const p = String(primaryStr || '').trim();
  let dt = parseFirstClassLineToEasternDateTime(p, studentTimezoneLabel);
  if (dt && dt.isValid) return dt;
  dt = parseSlotStringToEasternDateTime(p, studentTimezoneLabel);
  return dt && dt.isValid ? dt : null;
}

/** Normalize any stored First class / override string to Eastern formatting. */
export function displayPrimaryTimeSlot(value, studentTimezoneLabel) {
  const dt = parsePrimaryStringToEasternDateTime(value, studentTimezoneLabel);
  if (dt && dt.isValid) return formatFirstClassFromEasternDateTime(dt);
  return String(value || '');
}

export function formatFirstClassFromEasternDateTime(dt) {
  if (!dt || !dt.isValid) return '';
  const t = dt.setZone(DASHBOARD_TZ);
  const h = t.hour;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const period = h >= 12 ? 'PM' : 'AM';
  const min = t.minute;
  const timePart = min === 0 ? `${h12}${period}` : `${h12}:${String(min).padStart(2, '0')}${period}`;
  const suffix = 'EST';
  return `${t.toFormat('ccc')}, ${t.toFormat('MMMM')} ${t.day} @ ${timePart} ${suffix}`;
}

/** Admin picker: day-of-week + hour (24h) interpreted in US Eastern. */
export function formatFirstClassFromPicker(dayFullName, hour24) {
  const cal = firstCalendarDate2026Weekday(dayFullName);
  if (!cal) return '';
  const monthNum = MONTH_FULL_TO_NUM[cal.monthFull];
  const dt = DateTime.fromObject(
    { year: DEFAULT_SLOT_YEAR, month: monthNum, day: cal.dayNum, hour: Number(hour24), minute: 0, second: 0 },
    { zone: DASHBOARD_TZ }
  );
  return formatFirstClassFromEasternDateTime(dt);
}

/** ISO timestamps (e.g. last_updated) — one consistent pattern for the team. */
export function formatDashboardTimestamp(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: DASHBOARD_TZ,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  }).format(d);
}

/** Normalize a schedule line to Eastern for display in expanded row. */
export function formatSlotLineForDashboard(slotStr, studentTimezoneLabel) {
  const dt = parseSlotStringToEasternDateTime(slotStr, studentTimezoneLabel);
  if (dt) return formatFirstClassFromEasternDateTime(dt);
  return String(slotStr || '');
}

/** Sort key for First class column (ms in Eastern wall time). */
export function parseFirstClassSortKey(str, studentTimezoneLabel = 'Eastern Time (ET)') {
  const dt = parsePrimaryStringToEasternDateTime(str, studentTimezoneLabel);
  if (!dt || !dt.isValid) return null;
  return dt.toMillis();
}

export function slotMatchesPrimaryEastern(slotStr, primaryStr, studentTimezoneLabel) {
  if (!slotStr || !primaryStr) return true;
  const a = parseSlotStringToEasternDateTime(slotStr, studentTimezoneLabel);
  const b = parsePrimaryStringToEasternDateTime(primaryStr, studentTimezoneLabel);
  if (!a || !b) return true;
  return a.weekday === b.weekday && a.hour === b.hour && a.minute === b.minute;
}
